/**
 * Permission Service – Đọc quyền từ DB (roles.permissions_json)
 * Merge multi-role permissions cho user hiện tại
 * v2: Nhận allRoleCodes từ caller thay vì tự fetch
 */
import { supabase, handleError, type ServiceResult } from '@/services/base.service';
import type { UserRole } from '@/config/navigation.config';
import type { Module, Action } from '@/config/permission-matrix.config';
import { permissionMatrixConfig } from '@/config/permission-matrix.config';

export interface RolePermissions {
  role: UserRole;
  modules: Partial<Record<Module, Action[]>>;
}

export interface RoleMeta {
  landingPage?: string;
  quickActions?: string[];
  sidebarHidden?: string[];
}

export const permissionService = {
  /** Lấy permissions từ bảng role_permissions cho 1 role */
  async getRolePermissions(roleCode: UserRole): Promise<ServiceResult<Partial<Record<Module, Action[]>> | null>> {
    try {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('permissions (module, action)')
        .eq('roles.code', roleCode); // Giả định có join hoặc query qua role_id
      
      // Vì Supabase query join hơi phức tạp nếu không có quan hệ cụ thể trong schema cache, 
      // ta sẽ dùng cách đơn giản hơn: lấy role_id trước
      const { data: role } = await supabase.from('roles').select('id').eq('code', roleCode).maybeSingle();
      if (!role) return { data: null, error: null };

      const { data: permsData, error: permsError } = await supabase
        .from('role_permissions')
        .select('permissions(module, action)')
        .eq('role_id', role.id);

      if (permsError) throw permsError;

      const perms: Partial<Record<Module, Action[]>> = {};
      (permsData as any[]).forEach((p) => {
        const mod = p.permissions.module as Module;
        const action = p.permissions.action as Action;
        if (!perms[mod]) perms[mod] = [];
        perms[mod]!.push(action);
      });

      return { data: perms, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },

  /** Lấy role meta (landing page, quick actions, sidebar hidden) từ permissions_json cũ */
  async getRoleMeta(roleCode: UserRole): Promise<ServiceResult<RoleMeta | null>> {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('permissions_json')
        .eq('code', roleCode)
        .maybeSingle();
      if (error) throw error;
      const json = data?.permissions_json as Record<string, unknown> | null;
      if (!json) return { data: null, error: null };
      const meta: RoleMeta = {};
      if (json._landing) meta.landingPage = json._landing as string;
      if (json._quickActions) meta.quickActions = json._quickActions as string[];
      if (json._sidebarHidden) meta.sidebarHidden = json._sidebarHidden as string[];
      return { data: meta, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },

  /** Merge permissions: Gọi RPC fn_get_user_permissions (Default Deny / Priority Logic) */
  async getMergedPermissions(
    userId: string,
    primaryRole: UserRole,
    allRoleCodes: UserRole[]
  ): Promise<ServiceResult<Partial<Record<Module, Action[]>>>> {
    try {
      // 1. Gọi RPC lấy danh sách keys được phép
      const { data, error } = await supabase.rpc('fn_get_user_permissions', {
        p_user_id: userId,
      });

      if (error) throw error;

      // 2. Chuyển đổi list keys ['mod.action'] -> { mod: [action] }
      const keys = (data as { permission_key: string }[]).map((d) => d.permission_key);
      const result: Partial<Record<Module, Action[]>> = {};

      keys.forEach((key) => {
        const parts = key.split('.');
        if (parts.length !== 2) return;
        const mod = parts[0] as Module;
        const action = parts[1] as Action;
        if (!result[mod]) result[mod] = [];
        result[mod]!.push(action);
      });

      return { data: result, error: null };
    } catch (err) {
      console.error('[permissionService] getMergedPermissions failed:', err);
      return { data: {}, error: handleError(err) };
    }
  },

  /** Lưu permissions_json cho 1 role */
  async saveRolePermissions(
    roleCode: UserRole,
    perms: Partial<Record<Module, Action[]>>
  ): Promise<ServiceResult<null>> {
    try {
      // Đọc json hiện tại để giữ lại meta keys
      const { data: existing } = await supabase
        .from('roles')
        .select('permissions_json')
        .eq('code', roleCode)
        .maybeSingle();

      const existingJson = (existing?.permissions_json ?? {}) as Record<string, unknown>;
      const json: Record<string, unknown> = {};

      // Giữ lại meta keys
      if (existingJson._landing) json._landing = existingJson._landing;
      if (existingJson._quickActions) json._quickActions = existingJson._quickActions;
      if (existingJson._sidebarHidden) json._sidebarHidden = existingJson._sidebarHidden;

      // Thêm module permissions
      Object.entries(perms).forEach(([mod, actions]) => {
        if (actions && actions.length > 0) json[mod] = actions;
      });

      const { error } = await supabase
        .from('roles')
        .update({ permissions_json: json, updated_at: new Date().toISOString() })
        .eq('code', roleCode);

      if (error) throw error;
      return { data: null, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },

  /** Lưu role meta (landing page, quick actions, sidebar hidden) */
  async saveRoleMeta(
    roleCode: UserRole,
    meta: RoleMeta
  ): Promise<ServiceResult<null>> {
    try {
      // Đọc json hiện tại
      const { data: existing } = await supabase
        .from('roles')
        .select('permissions_json')
        .eq('code', roleCode)
        .maybeSingle();

      const json = { ...(existing?.permissions_json ?? {}) } as Record<string, unknown>;

      if (meta.landingPage !== undefined) {
        if (meta.landingPage) json._landing = meta.landingPage;
        else delete json._landing;
      }
      if (meta.quickActions !== undefined) {
        if (meta.quickActions.length > 0) json._quickActions = meta.quickActions;
        else delete json._quickActions;
      }
      if (meta.sidebarHidden !== undefined) {
        if (meta.sidebarHidden.length > 0) json._sidebarHidden = meta.sidebarHidden;
        else delete json._sidebarHidden;
      }

      const { error } = await supabase
        .from('roles')
        .update({ permissions_json: json, updated_at: new Date().toISOString() })
        .eq('code', roleCode);

      if (error) throw error;
      return { data: null, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },
  /** Lấy danh sách tất cả permissions định nghĩa trong hệ thống */
  async getAllPermissions(): Promise<ServiceResult<any[]>> {
    try {
      const { data, error } = await supabase
        .from('permissions')
        .select('*')
        .order('module', { ascending: true })
        .order('action', { ascending: true });
      if (error) throw error;
      return { data: data ?? [], error: null };
    } catch (err) {
      return { data: [], error: handleError(err) };
    }
  },

  /** Đồng bộ quyền cho Role hàng loạt */
  async syncRolePermissions(roleId: string, permissionIds: string[]): Promise<ServiceResult<null>> {
    try {
      const { error } = await supabase.rpc('fn_sync_role_permissions', {
        p_role_id: roleId,
        p_permission_ids: permissionIds,
      });
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },

  /** Đồng bộ quyền cho User hàng loạt (Overrides) */
  async syncUserPermissions(userId: string, overrides: { permission_id: string; is_denied: boolean }[]): Promise<ServiceResult<null>> {
    try {
      const { error } = await supabase.rpc('fn_sync_user_permissions', {
        p_user_id: userId,
        p_overrides: overrides,
      });
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },
  /** Lấy danh sách các mẫu quyền chuẩn (Presets) */
  async getPresets(): Promise<ServiceResult<any[]>> {
    try {
      const { data, error } = await supabase
        .from('permission_presets')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      return { data: data ?? [], error: null };
    } catch (err) {
      return { data: [], error: handleError(err) };
    }
  },

  /** Áp dụng mẫu quyền vào một Vai trò cụ thể */
  async applyPresetToRole(presetId: string, roleId: string): Promise<ServiceResult<null>> {
    try {
      const { error } = await supabase.rpc('fn_apply_preset_to_role', {
        p_preset_id: presetId,
        p_role_id: roleId,
      });
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) {
      return { data: null, error: handleError(err) };
    }
  },
};
